// LLM generated - Model Claude 4 Sonnet
/**
 * Contest Chat - Using React's useOptimistic + useTransition
 *
 * This is a simplified version that removes the need for:
 * - useOptimisticReactions hook and its merge functions
 * - Complex prop drilling through useContestChatData
 * - Manual pending state management
 *
 * The optimistic updates are now handled directly in useContestChatState.
 */
import { useContext, useEffect, useMemo, useDeferredValue, useRef, useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useQueryClient } from '@tanstack/react-query';

import ChatDrawer from '~/domains/design-system/components/Chat/ChatDrawer/ChatDrawer';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useAvailableReactions } from '~/services/live-activity/hooks/useAvailableReactions';
import { getTimeAgoTranslation } from '~/components/utils/formatters';
import type {
  ChatMessage,
  ChatMessagesInfiniteData,
  GetMessagesResponse,
} from '~/services/live-activity/types';

import { ContestChatModalContext } from './ContestChatModals/ContestChatModalContext';
import type { ContestChatProps } from './types';
import { useContestChatContext } from './ContestChatContext';
import { useContestChatActions } from './hooks/useContestChatActions';
import { usePostsReactions } from './hooks/usePostsReactions';
import { adaptApiMessageToDesignSystem, adaptApiMessageToPost } from './utils/contestChatUtils';
import { createQueryKey, MESSAGE_PAGE_SIZE } from './constants/queryKeys';

export function ContestChat({ isSidePanel = false, onModalClose }: ContestChatProps) {
  const { t } = useTranslation('chat');
  const { openModal } = useContext(ContestChatModalContext);

  // Get ALL data from context - optimistic updates are built-in
  const {
    contestId,
    isCommissioner,
    user,
    activeTab,
    setActiveTab,
    messagesChat,
    postsChat,
    chatId,
    isLoadingChats,
    finalIsChatGloballyEnabled,
    finalIsChatWebEnabled,
    finalIsPostsWebEnabled,
    isChatEnabled,
    isPostsEnabled,
    hasReceivedChatPusherMessage,
    hasReceivedPostPusherMessage,
    // Messages and posts now have optimistic updates applied
    messagesChatHistory,
    isLoadingMessagesChat,
    postsChatHistory,
    isLoadingPostsChat,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sendChatMessage,
    sendPostMessage,
    pinMessage,
    unpinMessage,
    isPinUnpinLoading,
    // Reaction handlers - now from state hook
    handleAddReaction,
    handleRemoveReaction,
    unreadMessagesData,
    unreadPostsData,
    // Mark as read - now smarter, only called when needed
    markAsReadMessages,
    markAsReadPosts,
    getLastSentMessagesMessageId,
    getLastSentPostsMessageId,
    hasUnreadSinceLastViewRef,
    // Chat open state (desktop side panel or mobile drawer)
    isChatOpen,
  } = useContestChatContext();

  // Fetch available reactions
  const { data: availableReactions, isLoading: isLoadingReactions } = useAvailableReactions();

  // UI action handlers (report, edit, delete, mute, etc.)
  const {
    handleReportClick,
    handleEditPost,
    handleDeletePost,
    handleDeleteMessage,
    handleMutePost,
    handleOpenPostModal,
    handleModalConfirm,
    updateChatMutation,
    localMuteStatus,
  } = useContestChatActions({
    contestId,
    messagesChat,
    postsChat,
    chatId,
    activeTab,
    scrollContainerRef: undefined,
    toggleChatAction: undefined,
    user,
    sendPostMessage, // Pass optimistic post sender
  });

  const handleToggleChatEnabled = useCallback(() => {
    openModal({
      type: 'DisableChatModal',
      isCurrentlyEnabled: isChatEnabled,
      onConfirm: () => handleModalConfirm(isChatEnabled),
      isLoading: updateChatMutation.isLoading,
    });
  }, [openModal, isChatEnabled, handleModalConfirm, updateChatMutation.isLoading]);

  // Fetch individual reactions for posts - needed because the posts API is missing this
  // return userReactions populated (unlike chat messages)
  const queryClient = useQueryClient();
  const postMessageIds = postsChatHistory
    .filter((msg) => !msg.id.startsWith('optimistic-'))
    .map((msg) => msg.id);
  const deferredPostMessageIds = useDeferredValue(postMessageIds);
  const { userReactionsMap: postsUserReactionsMap } = usePostsReactions({
    chatId: postsChat?.id,
    messageIds: deferredPostMessageIds,
    currentUserId: user?.id,
    enabled: !!postsChat?.id && deferredPostMessageIds.length > 0,
  });

  // Track which posts we've already seeded with userReactions from the API
  const seededPostIdsRef = useRef<Set<string>>(new Set());
  const previousPostsChatIdRef = useRef<string | undefined>(undefined);

  // Clear seeded posts when switching to a different posts chat
  useEffect(() => {
    if (postsChat?.id !== previousPostsChatIdRef.current) {
      seededPostIdsRef.current.clear();
      previousPostsChatIdRef.current = postsChat?.id;
    }
  }, [postsChat?.id]);

  // Seed the cache with API-fetched userReactions on initial load
  // This makes the cache the single source of truth for userReactions
  useEffect(() => {
    if (!postsChat?.id || Object.keys(postsUserReactionsMap).length === 0) return;

    const queryKey = createQueryKey.chatMessages(postsChat.id, MESSAGE_PAGE_SIZE, false);

    // Find posts that have API reactions but haven't been seeded yet
    const postsToSeed = Object.entries(postsUserReactionsMap).filter(
      ([messageId, reactions]) => reactions.length > 0 && !seededPostIdsRef.current.has(messageId)
    );

    if (postsToSeed.length === 0) return;

    // Update cache with the API-fetched userReactions
    queryClient.setQueryData<ChatMessagesInfiniteData>(queryKey, (oldData) => {
      if (!oldData?.pages) return oldData;

      const newPages = oldData.pages.map((page: GetMessagesResponse) => ({
        ...page,
        data: (page.data || []).map((msg: ChatMessage) => {
          const apiReactions = postsUserReactionsMap[msg.id];
          // Only seed if we have API data and haven't seeded this post yet
          if (apiReactions && apiReactions.length > 0 && !seededPostIdsRef.current.has(msg.id)) {
            seededPostIdsRef.current.add(msg.id);
            return { ...msg, userReactions: apiReactions };
          }
          return msg;
        }),
      }));

      return { ...oldData, pages: newPages };
    });
  }, [postsChat?.id, postsUserReactionsMap, queryClient]);

  const handlePinPost = useCallback(
    (messageId: string, isPinned: boolean) => {
      if (isPinned) {
        unpinMessage(messageId);
      } else {
        openModal({
          type: 'ConfirmModal',
          variant: 'pin',
          onConfirm: () => pinMessage(messageId),
          isSubmitting: isPinUnpinLoading,
        });
      }
    },
    [unpinMessage, openModal, pinMessage, isPinUnpinLoading]
  );

  const formatTimeAgo = useCallback(
    (timestamp: string): string => {
      const timeAgoData = getTimeAgoTranslation(timestamp);
      try {
        return t(`commissioner:${timeAgoData.key}`, { count: timeAgoData.interval || 1 });
      } catch {
        const { interval } = timeAgoData;
        if (timeAgoData.key.includes('year')) return `${interval}y ago`;
        if (timeAgoData.key.includes('month')) return `${interval}mo ago`;
        if (timeAgoData.key.includes('day')) return `${interval}d ago`;
        if (timeAgoData.key.includes('hour')) return `${interval}h ago`;
        if (timeAgoData.key.includes('minute')) return `${interval}m ago`;
        if (timeAgoData.key.includes('second')) return `${interval}s ago`;
        if (timeAgoData.key.includes('justNow')) return 'now';
        return timestamp;
      }
    },
    [t]
  );

  // Transform data for design system components
  // SIMPLIFIED: No more optimistic merge functions needed - data already has optimistic updates
  const allChatMessages = useMemo(
    () => messagesChatHistory.filter((message) => !message.deletedAt),
    [messagesChatHistory]
  );

  const allPostMessages = useMemo(
    () => postsChatHistory.filter((message) => !message.deletedAt),
    [postsChatHistory]
  );

  const sortedPostMessages = useMemo(
    () =>
      [...allPostMessages].sort((a, b) => {
        const aPinned = a.isPinned || false;
        const bPinned = b.isPinned || false;
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;
        return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
      }),
    [allPostMessages]
  );

  // Convert to design system format - no optimistic options needed
  const designSystemMessages = useMemo(() => {
    const currentTabMessages = activeTab === 'posts' ? sortedPostMessages : allChatMessages;
    return currentTabMessages.map((message) =>
      adaptApiMessageToDesignSystem(message, user?.handle)
    );
  }, [activeTab, sortedPostMessages, allChatMessages, user?.handle]);

  // Convert to Post component format
  const postComponentData = useMemo(
    () =>
      sortedPostMessages.map((message) =>
        adaptApiMessageToPost(
          message,
          formatTimeAgo(message.publishedAt),
          user?.handle,
          handleEditPost,
          handleDeletePost,
          handlePinPost,
          handleMutePost,
          handleReportClick,
          postsChat?.id,
          isCommissioner,
          localMuteStatus ?? false
        )
      ),
    [
      sortedPostMessages,
      user?.handle,
      postsChat?.id,
      isCommissioner,
      formatTimeAgo,
      handleEditPost,
      handleDeletePost,
      handlePinPost,
      handleMutePost,
      handleReportClick,
      localMuteStatus,
    ]
  );

  // Pre-compute Maps for O(1) user reaction lookups
  const messagesUserReactionsMap = useMemo(
    () => new Map(messagesChatHistory.map((msg) => [msg.id, msg.userReactions || []])),
    [messagesChatHistory]
  );

  const postsUserReactionsLookupMap = useMemo(
    () => new Map(postsChatHistory.map((msg) => [msg.id, msg.userReactions || []])),
    [postsChatHistory]
  );

  // Get user reactions - O(1) lookup via Map
  // Cache is the single source of truth, seeded with API data and updated optimistically
  const getUserReactions = useCallback(
    (messageId: string): string[] => {
      if (activeTab === 'posts') {
        return postsUserReactionsLookupMap.get(messageId) || [];
      }
      return messagesUserReactionsMap.get(messageId) || [];
    },
    [activeTab, messagesUserReactionsMap, postsUserReactionsLookupMap]
  );

  // Find latest real message (skip optimistic) - memoized for efficiency
  const latestRealMessage = useMemo(
    () => allChatMessages.find((msg) => !msg.id.startsWith('optimistic-')),
    [allChatMessages]
  );

  // Find latest real post (skip optimistic) - memoized for efficiency
  const latestRealPost = useMemo(() => {
    const realPosts = postsChatHistory.filter((post) => !post.id.startsWith('optimistic-'));
    if (!realPosts.length) return null;
    return realPosts.reduce((latest, post) => {
      if (!latest) return post;
      return new Date(post.publishedAt) > new Date(latest.publishedAt) ? post : latest;
    }, realPosts[0]);
  }, [postsChatHistory]);

  // --- Mark As Read Logic ---
  // Only fires in these scenarios:
  // 1. Initial load: When we first have messages and haven't sent markAsRead for this message yet
  // 2. Tab switch: When switching to a tab that received new messages while away
  // Note: New Pusher messages while viewing the tab are handled in handleMessageCreated

  // Track if we've done initial markAsRead for each tab
  const hasInitializedMessagesRef = useRef(false);
  const hasInitializedPostsRef = useRef(false);

  // Reset initialization flags when chat closes
  useEffect(() => {
    if (!isChatOpen) {
      hasInitializedMessagesRef.current = false;
      hasInitializedPostsRef.current = false;
    }
  }, [isChatOpen]);

  // Messages tab: Initial load OR tab switch with unread
  useEffect(() => {
    if (activeTab !== 'messages' || !latestRealMessage?.id) return;

    const lastSentId = getLastSentMessagesMessageId();
    const hasNewMessages = hasUnreadSinceLastViewRef.current.messages;
    const needsInitialMark =
      !hasInitializedMessagesRef.current && lastSentId !== latestRealMessage.id;

    if (needsInitialMark || hasNewMessages) {
      markAsReadMessages(latestRealMessage.id);
      hasInitializedMessagesRef.current = true;
      hasUnreadSinceLastViewRef.current.messages = false;
    }
  }, [
    activeTab,
    latestRealMessage?.id,
    getLastSentMessagesMessageId,
    markAsReadMessages,
    hasUnreadSinceLastViewRef,
  ]);

  // Posts tab: Initial load OR tab switch with unread
  useEffect(() => {
    if (activeTab !== 'posts' || !latestRealPost?.id) return;

    const lastSentId = getLastSentPostsMessageId();
    const hasNewPosts = hasUnreadSinceLastViewRef.current.posts;
    const needsInitialMark = !hasInitializedPostsRef.current && lastSentId !== latestRealPost.id;

    if (needsInitialMark || hasNewPosts) {
      markAsReadPosts(latestRealPost.id);
      hasInitializedPostsRef.current = true;
      hasUnreadSinceLastViewRef.current.posts = false;
    }
  }, [
    activeTab,
    latestRealPost?.id,
    getLastSentPostsMessageId,
    markAsReadPosts,
    hasUnreadSinceLastViewRef,
  ]);

  // Infinite scroll - trigger prefetch when ~4 messages from top (500px)
  const { loaderRef, scrollContainerRef } = useInfiniteScroll({
    fetchMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: hasNextPage,
    distance: 500,
  });

  // Tab change handler
  const handleTabChange = (tab: 'messages' | 'posts') => {
    setActiveTab(tab);
  };

  // Message submit handlers
  const handleChatMessageSubmit = (content: string) => {
    sendChatMessage({ message: content });
  };

  const handlePostSubmit = (content: string) => {
    sendPostMessage({ message: content, isPost: true });
  };

  return (
    <ChatDrawer
      title={t('title')}
      messages={designSystemMessages}
      posts={postComponentData}
      currentUserHandle={user?.handle}
      isCommissioner={isCommissioner}
      onToggleChatEnabled={isCommissioner ? handleToggleChatEnabled : undefined}
      isChatGloballyEnabled={finalIsChatGloballyEnabled}
      isChatEnabled={isChatEnabled}
      isPostsEnabled={isPostsEnabled}
      isChatWebEnabled={finalIsChatWebEnabled}
      isPostsWebEnabled={finalIsPostsWebEnabled}
      hasUnreadMessages={
        (unreadMessagesData?.data?.count ?? 0) > 0 || (unreadPostsData?.data?.count ?? 0) > 0
      }
      hasReceivedChatPusherMessage={hasReceivedChatPusherMessage}
      hasReceivedPostPusherMessage={hasReceivedPostPusherMessage}
      onChatMessageSubmit={handleChatMessageSubmit}
      onPostSubmit={handlePostSubmit}
      onOpenPostModal={handleOpenPostModal}
      onReportClick={handleReportClick}
      onDeleteClick={handleDeleteMessage}
      onReactionSelect={handleAddReaction}
      onReactionRemove={handleRemoveReaction}
      availableReactions={availableReactions?.data || []}
      isLoadingReactions={isLoadingReactions}
      getUserReactions={getUserReactions}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      loaderRef={loaderRef}
      scrollContainerRef={scrollContainerRef}
      onClose={onModalClose}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isSidePanel={isSidePanel}
      isLoadingChats={isLoadingChats}
      isLoadingMessages={isLoadingMessagesChat}
      isLoadingPosts={isLoadingPostsChat}
    />
  );
}
